/**
 * @file api-labels.ts
 * @description Admin API-клиент печати складских этикеток (phase19.22,
 *   docs/api/wms-labels.md; правила — inventory.md W17-4, W17-5a/5b/5c,
 *   W17-9a, W17-9c, W20).
 *
 * Зеркалит `apps/api/src/wms/labels/**`: печать (PDF в хранилище, ссылка на
 * час), журнал и перепечатка, блок «Этикетки» проведённой приёмки, партии
 * фасовки для печати из карточки товара, калибровка листов склада.
 *
 * Коды ошибок, которые разбирает UI:
 *  - 502 `label_render_failed` — Gotenberg/хранилище недоступны: печать не
 *    случилась, но приёмка и выбор в диалоге не пострадали;
 *  - 422 `code_mode_not_supported` — «только штрих» на мини-этикетке;
 *  - 409 `receipt_not_committed` — печать черновика (W17-5a);
 *  - 422 `label_sheet_overflow` — сетка калибровки вылезает за A4.
 *
 * Этикетки ячеек, полок и стеллажей (phase19.22 шаг 5) — отдельный вход
 * `POST /admin/wms/labels/print-bins`: у них нет фасовки и партии, только
 * адрес `LOC:…`. В журнале такие задачи — `source=BINS`.
 */

import { apiDownload, apiFetch, type ApiFileResponse } from "./api-client";

// ─── Types ────────────────────────────────────────────────────────────────────

/** Шаблон складской этикетки (W17-5b). */
export type LabelTemplate = "STANDARD" | "COMPACT" | "MINI";

/** Какие коды печатать на этикетке (W17-5c). */
export type LabelCodeMode = "BOTH" | "DATAMATRIX_ONLY" | "CODE128_ONLY";

/** Статус этикеток партии (W17-9c). */
export type LotLabelStatus = "NONE" | "PRINTED" | "STALE";

/** Откуда печатаем: приёмка, журнал остатков или карточка фасовки. */
export type LabelPrintSource = "RECEIPT" | "STOCK" | "SKU";

/** Источник задачи в журнале: печать товара или этикетки ячеек (`BINS`). */
export type LabelJobSource = LabelPrintSource | "BINS";

/** «Начать с N-й наклейки» — отдельно для каждого шаблона (разные листы). */
export type LabelStartAt = Partial<Record<LabelTemplate, number>>;

/** Строка запроса печати. */
export interface LabelPrintItemInput {
  skuId: string;
  /** `null` — только для `source=SKU` («без партии»). */
  lotId?: string | null;
  /** Только для `source=RECEIPT`. */
  receiptItemId?: string | null;
  /** 1..2000, всего ≤ 5000. */
  quantity: number;
  template?: LabelTemplate;
  codeMode?: LabelCodeMode;
}

/** Тело `POST /admin/wms/labels/print`. */
export interface LabelPrintInput {
  source: LabelPrintSource;
  warehouseId: string;
  receiptId?: string | null;
  items: LabelPrintItemInput[];
  startAt?: LabelStartAt;
  printerDeviceId?: string | null;
  codeMode?: LabelCodeMode;
  /** Пунктир по границе наклеек — печать на обычной бумаге (настройка рабочего места). */
  cutLines?: boolean;
}

/** Готовый файл печати (сейчас всегда один на задачу). */
export interface LabelPrintFile {
  templates: LabelTemplate[];
  /** Presigned-ссылка на час. */
  url: string;
  fileName: string;
  sheets: number;
}

/** Сводка по шаблону в ответе печати. */
export interface LabelPrintPerTemplate {
  template: LabelTemplate;
  title: string;
  startAt: number;
  sheets: number;
  labels: number;
  /** С какой наклейки продолжать начатый лист (1 — лист израсходован). */
  nextStartAt: number;
}

/** Ответ печати и перепечатки. */
export interface LabelPrintResult {
  jobId: string;
  labelsCount: number;
  sheetsCount: number;
  files: LabelPrintFile[];
  perTemplate: LabelPrintPerTemplate[];
  warnings: Array<{
    code: "code128_only_no_lot";
    message: string;
    details: { lineNos: number[] };
  }>;
}

/**
 * Файл задачи журнала: доступен / скрыт (устарел по W17-9c или скрыт вручную,
 * `POST jobs/:id/hide`) / нет (сбой).
 */
export type LabelJobFile =
  | { state: "available"; url: string; fileName: string; sizeBytes: number }
  | { state: "hidden"; hiddenAt: string }
  | { state: "none" };

/** Строка журнала печати. */
export interface LabelPrintJobDto {
  id: string;
  createdAt: string;
  status: "DONE" | "FAILED";
  source: LabelJobSource;
  warehouseId: string;
  receiptId: string | null;
  createdBy: { id: string; displayName: string };
  printerDeviceId: string | null;
  labelsCount: number;
  sheetsCount: number;
  itemsCount: number;
  templates: LabelTemplate[];
  /** Колонка «Строки» в приёмке. */
  receiptLineNos: number[];
  /** Только `source=BINS`: что попало в печать адресов. */
  binSummary?: BinJobSummaryDto | null;
  file: LabelJobFile;
  error: string | null;
}

/** Сводка задачи печати этикеток ячеек (журнал, `source=BINS`). */
export interface BinJobSummaryDto {
  linesCount: number;
  binsCount: number;
  shelvesCount: number;
  racksCount: number;
  racks: string[];
  templates: LabelTemplate[];
}

/** Шаблон этикетки ячейки: мини нет — адрес с кодом на 20×20 не читается. */
export type BinLabelTemplate = Exclude<LabelTemplate, "MINI">;

/**
 * Тело `POST /admin/wms/labels/print-bins`. Либо `binIds` (отмеченные
 * ячейки), либо `rack` (стеллаж целиком — сервер сам возьмёт его активные
 * ячейки); можно оба — сервер объединит. `template` — шаблон ячеек и
 * полок; стеллажная — всегда «Стандарт». `copies` — копий КАЖДОЙ этикетки
 * (ячейки, полки, стеллажа). Лимит — 500 этикеток с учётом копий.
 * `codeMode` по умолчанию BOTH: режим склада для товаров сюда не наследуется.
 */
export interface BinLabelPrintInput {
  warehouseId: string;
  binIds?: string[];
  rack?: string;
  includeShelfLabels?: boolean;
  includeRackLabel?: boolean;
  /** Копий каждой этикетки, 1..5. */
  copies?: number;
  template?: BinLabelTemplate;
  startAt?: LabelStartAt;
  codeMode?: LabelCodeMode;
  printerDeviceId?: string | null;
  /** Пунктир по границе наклеек — печать на обычной бумаге. */
  cutLines?: boolean;
}

/** Ответ печати ячеек — как у `print` + число разных ячеек. */
export interface BinLabelPrintResult extends LabelPrintResult {
  binsCount: number;
}

/** Блок «Этикетки» проведённой приёмки. */
export interface ReceiptLabelStatusDto {
  receiptId: string;
  totalItems: number;
  printedItems: number;
  staleItems: number;
  notPrintedItems: number;
  missingExpiryItems: number;
  /** «История печати (N)». */
  jobsCount: number;
  lastPrintedAt: string | null;
  items: Array<{
    receiptItemId: string;
    lineNo: number;
    skuId: string;
    lotId: string | null;
    lotCode: string | null;
    labelStatus: LotLabelStatus | null;
    labelsPrintedAt: string | null;
    /** Принято по строке. */
    quantity: string;
    expiryMissing: boolean;
  }>;
}

/** Партия с остатком для печати из карточки фасовки. */
export interface SkuLabelLotDto {
  lotId: string;
  code: string;
  batchNumber: string;
  expiresAt: string | null;
  expiryMissing: boolean;
  labelStatus: LotLabelStatus;
  labelsPrintedAt: string | null;
  warehouseId: string;
  warehouseName: string;
  quantity: string;
  bin: { id: string; code: string } | null;
}

/** Ответ `GET /admin/wms/labels/sku/:skuId/lots`. */
export interface SkuLabelLotsDto {
  sku: {
    id: string;
    code: string;
    displayName: string;
    productName: string;
    internalCode: string;
    labelTemplate: LabelTemplate | null;
    effectiveTemplate: LabelTemplate;
    templateSource: "sku" | "kind" | "default";
  };
  lots: SkuLabelLotDto[];
}

/** Поля и зазоры листа, мм. */
export interface LabelSheetMargins {
  marginTopMm: number;
  marginLeftMm: number;
  gapXMm: number;
  gapYMm: number;
}

/** Лист самоклейки склада по шаблону. */
export interface LabelSheetDto extends LabelSheetMargins {
  template: LabelTemplate;
  title: string;
  widthMm: number;
  heightMm: number;
  cols: number;
  rows: number;
  perSheet: number;
  defaults: LabelSheetMargins;
  /** Склад менял калибровку этого шаблона. */
  customized: boolean;
  /** С какой наклейки продолжить по последней печати склада. */
  nextStartAt: number;
}

/** Настройки этикеток склада. */
export interface LabelSheetsDto {
  warehouseId: string;
  defaultLabelPrinterId: string | null;
  /** Только чтение: хранения дефолта пока нет (всегда BOTH). */
  defaultCodeMode: LabelCodeMode;
  sheets: LabelSheetDto[];
}

// ─── Endpoints ────────────────────────────────────────────────────────────────

export const labelsApi = {
  /** Сформировать PDF этикеток и отметить партии «напечатаны». */
  print: (input: LabelPrintInput) =>
    apiFetch<LabelPrintResult>("/admin/wms/labels/print", { method: "POST", json: input }),
  /** Этикетки ячеек, полок и стеллажа (PDF в хранилище, ссылка на час). */
  printBins: (input: BinLabelPrintInput) =>
    apiFetch<BinLabelPrintResult>("/admin/wms/labels/print-bins", { method: "POST", json: input }),
  /** Журнал печати, новые сверху (до 50); `source=BINS` — этикетки ячеек. */
  jobs: (params: {
    receiptId?: string;
    warehouseId?: string;
    skuId?: string;
    lotId?: string;
    source?: LabelJobSource;
  }) => {
    const q = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) if (value) q.set(key, value);
    const qs = q.toString();
    return apiFetch<LabelPrintJobDto[]>(`/admin/wms/labels/jobs${qs ? `?${qs}` : ""}`);
  },
  job: (id: string) => apiFetch<LabelPrintJobDto>(`/admin/wms/labels/jobs/${id}`),
  /** Перепечатать по строкам старой задачи с текущими данными партий. */
  reprint: (id: string, input: { startAt?: LabelStartAt; printerDeviceId?: string | null } = {}) =>
    apiFetch<LabelPrintResult>(`/admin/wms/labels/jobs/${id}/reprint`, {
      method: "POST",
      json: input,
    }),
  /**
   * Скрыть файл задачи из журнала (`fileHiddenAt`): запись о печати остаётся,
   * перепечатка доступна. Ответ не разбираем — журнал после мутации перечитываем.
   */
  hideJob: (id: string) =>
    apiFetch<unknown>(`/admin/wms/labels/jobs/${id}/hide`, { method: "POST", json: {} }),
  /** Блок «Этикетки» проведённой приёмки. */
  receiptStatus: (receiptId: string) =>
    apiFetch<ReceiptLabelStatusDto>(`/admin/wms/labels/receipt/${receiptId}/status`),
  /** Партии фасовки с остатком — печать из карточки товара. */
  skuLots: (skuId: string, warehouseId?: string) =>
    apiFetch<SkuLabelLotsDto>(
      `/admin/wms/labels/sku/${skuId}/lots${warehouseId ? `?warehouseId=${warehouseId}` : ""}`,
    ),
  labelSheets: (warehouseId: string) =>
    apiFetch<LabelSheetsDto>(`/admin/wms/warehouses/${warehouseId}/label-sheets`),
  saveLabelSheets: (
    warehouseId: string,
    sheets: Array<LabelSheetMargins & { template: LabelTemplate }>,
  ) =>
    apiFetch<LabelSheetsDto>(`/admin/wms/warehouses/${warehouseId}/label-sheets`, {
      method: "PUT",
      json: { sheets },
    }),
  /** Пробная страница: рамки наклеек 1…N; значения формы — поверх сохранённых. */
  calibrationPdf: (
    warehouseId: string,
    template: LabelTemplate,
    margins?: Partial<LabelSheetMargins>,
  ): Promise<ApiFileResponse> =>
    apiDownload(
      `/admin/wms/warehouses/${warehouseId}/label-sheets/${template}/calibration.pdf`,
      { method: "POST", json: margins ?? {}, headers: { Accept: "application/pdf" } },
      `calibration-${template.toLowerCase()}.pdf`,
    ),
};

// ─── Реестры подписей ─────────────────────────────────────────────────────────

/** Шаблоны — данными (W17-5b): новый формат самоклейки = запись здесь. */
export const LABEL_TEMPLATE_META: Record<
  LabelTemplate,
  { title: string; short: string; perSheet: number; cols: number; rows: number; hasCode128: boolean }
> = {
  STANDARD: { title: "Стандарт 70×37", short: "стандарт", perSheet: 24, cols: 3, rows: 8, hasCode128: true },
  COMPACT: { title: "Компакт 52.5×29.7", short: "компакт", perSheet: 40, cols: 4, rows: 10, hasCode128: true },
  MINI: { title: "Мини 20×20", short: "мини", perSheet: 140, cols: 10, rows: 14, hasCode128: false },
};

/** Порядок групп шаблонов — как в PDF сервера. */
export const LABEL_TEMPLATE_ORDER: LabelTemplate[] = ["STANDARD", "COMPACT", "MINI"];

/** Режимы кодов (W17-5c). */
export const LABEL_CODE_MODE_META: Record<LabelCodeMode, { label: string }> = {
  BOTH: { label: "Оба" },
  DATAMATRIX_ONLY: { label: "DataMatrix" },
  CODE128_ONLY: { label: "Штрих" },
};
